import { Link } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { ContentSection, SectionHeader } from '../components/sections/ContentSection';
import { BookOpen, Users, Heart, Clock, ArrowRight, Loader2 } from 'lucide-react';
import { usePageData } from '../hooks/usePageData';
import { SEO } from '../components/SEO';
import { sanitizeHTML } from '../utils/sanitize';

const iconMap = {
  BookOpen: BookOpen,
  Users: Users,
  Heart: Heart,
  Clock: Clock
};

export default function SundaySchool() {
  const { pageData, loading, error } = usePageData('sunday-school');

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-secondary" />
        </div>
      </Layout>
    );
  }

  if (error || !pageData) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <p className="text-slate-500">Failed to load page content</p>
        </div>
      </Layout>
    );
  }

  const { hero, sections, cta, seo } = pageData;
  const overview = sections?.overview || {};
  const classes = sections?.classes || [];
  const features = sections?.features || [];

  return (
    <Layout>
      <SEO 
        title={seo?.title || hero?.title}
        description={seo?.description || hero?.subtitle}
        image={seo?.image || hero?.image}
        url="/sunday-school"
      />
      
      {/* Hero Banner */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={hero?.image}
            alt={hero?.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4" data-testid="sunday-school-page-title">
            {hero?.title}
          </h1>
          <p className="text-xl text-slate-200 max-w-2xl mx-auto">
            {hero?.subtitle}
          </p>
        </div>
      </section>

      {/* Overview */}
      <ContentSection bgColor="bg-white">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            {overview.tagline && (
              <p className="text-secondary font-semibold text-sm tracking-wide uppercase mb-4">
                {overview.tagline}
              </p>
            )}
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-primary-900 mb-6">
              {overview.heading}
            </h2>
            <div 
              className="text-lg text-slate-600 leading-relaxed mb-6 prose prose-a:text-secondary"
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(overview.description1) }}
            />
            <div 
              className="text-slate-600 leading-relaxed mb-8 prose prose-a:text-secondary"
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(overview.description2) }}
            />
            <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
              <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-secondary" />
              </div>
              <div>
                <p className="font-semibold text-primary-900">{overview.timeLabel}</p>
                <p className="text-slate-600">{overview.timeValue}</p>
              </div>
            </div>
          </div>
          <div>
            <img 
              src={overview.image || hero?.image}
              alt="Sunday School class"
              className="rounded-2xl shadow-xl w-full"
            />
          </div>
        </div>
      </ContentSection>

      {/* Class Offerings */}
      {classes.length > 0 && (
        <ContentSection bgColor="bg-slate-50">
          <SectionHeader 
            title="Our Classes"
            subtitle="Age-appropriate learning for everyone in your family"
          />
          <div className="grid md:grid-cols-2 gap-6">
            {classes.map((classItem) => (
              <div 
                key={`class-${classItem.title}`}
                className="bg-white rounded-xl p-8 hover:shadow-lg transition-all border-l-4 border-secondary"
                data-testid={`class-card-${classItem.title}`}
              >
                <h3 className="font-heading text-xl font-semibold text-primary-900 mb-3">
                  {classItem.title}
                </h3>
                <div 
                  className="text-slate-600 leading-relaxed mb-4 prose prose-a:text-secondary"
                  dangerouslySetInnerHTML={{ __html: sanitizeHTML(classItem.description) }}
                />
                <div className="flex items-center gap-2 text-sm text-primary-900 font-medium">
                  <Clock className="w-4 h-4" />
                  {classItem.time}
                </div>
              </div>
            ))}
          </div>
        </ContentSection>
      )}

      {/* Features */}
      {features.length > 0 && (
        <ContentSection bgColor="bg-white">
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature) => {
              const IconComponent = iconMap[feature.icon] || Heart;
              return (
                <div 
                  key={`feature-${feature.title}`}
                  className="text-center"
                  data-testid={`feature-${feature.title}`}
                >
                  <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <IconComponent className="w-8 h-8 text-secondary" />
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-primary-900 mb-3">{feature.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </ContentSection>
      )}

      {/* CTA */}
      {cta?.show && (
        <ContentSection bgColor={cta.bgColor || "bg-primary-900"} paddingY="py-16">
          <div className="text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">
              {cta.title}
            </h2>
            <p className="text-slate-300 text-lg mb-8 max-w-xl mx-auto">
              {cta.description}
            </p>
            <Link
              to={cta.buttonLink || "/contact"}
              className="inline-flex items-center gap-2 bg-secondary text-white px-8 py-4 rounded-full font-semibold hover:bg-secondary-600 transition-all hover:shadow-lg group"
              data-testid="sunday-school-contact-btn"
            >
              {cta.buttonText || "Contact Us for More Info"}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </ContentSection>
      )}
    </Layout>
  );
}
